import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight, Gear } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditShizai } from '@/auth/AuthContext';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { FormSection } from '@/components/enterprise/FormSection';
import { shizaiApi } from './shizaiApi';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Presentation labels only — the board owns the workflow logic.
const KANBAN_COLUMNS = [
  { key: 'open', label: '依頼中' },
  { key: 'ordered', label: '発注済' },
  { key: 'partial', label: '一部入荷' },
  { key: 'received', label: '入荷済' },
  { key: 'closed', label: '完了' },
];

const RELATED = [
  { to: '/settings/shizai-approvals', label: '承認ルート', desc: '金額しきい値ごとの承認者・代理承認者' },
  { to: '/settings/shizai-delegations', label: '承認の委任', desc: '不在時の委任先と期間' },
  { to: '/settings/shizai-notifications', label: '通知', desc: '依頼・発注・入荷のメール通知' },
  { to: '/settings/order-categories', label: '受注分類', desc: '発注に付与する分類マスター' },
  { to: '/settings/delivery-destinations', label: '納品先', desc: '発注書に印字する納品先' },
];

/**
 * 資材 › 設定 — the single home for 資材 administration (Jun 2026).
 * Aggregates settings that used to hide inside operational screens
 * (発注一覧 の歯車, かんばん の設定) and links to the platform 設定 screens
 * that already own their own data. No logic is duplicated: the same
 * /api/shizai/config and /api/shizai/kanban-settings endpoints are used.
 */
export default function ShizaiSettings() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditShizai(user);

  const [cfg, setCfg] = useState(null);
  const [cfgBusy, setCfgBusy] = useState(false);
  const [limits, setLimits] = useState({});
  const [wipBusy, setWipBusy] = useState(false);

  useEffect(() => { shizaiApi.getConfig().then(setCfg).catch(() => toast.error(t('shizai.error'))); }, [t]);
  useEffect(() => {
    axios.get(`${API_ROOT}/shizai/kanban-settings`)
      .then((r) => setLimits(r.data?.wip_limits || {}))
      .catch(() => {});
  }, []);

  const saveConfig = async () => {
    setCfgBusy(true);
    try {
      const next = await shizaiApi.setConfig({
        po_approval_enabled: cfg.po_approval_enabled,
        po_approval_threshold: Math.max(0, Number(cfg.po_approval_threshold) || 0),
        require_order_category_on_po: !!cfg.require_order_category_on_po,
        receipt_photo_retention_days: Math.max(0, Math.min(3650, Number(cfg.receipt_photo_retention_days) || 0)),
      });
      setCfg(next);
      toast.success(t('shizai.saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('shizai.error')); }
    finally { setCfgBusy(false); }
  };

  const saveWip = async () => {
    setWipBusy(true);
    try {
      const cleaned = Object.fromEntries(
        KANBAN_COLUMNS.map((c) => [c.key, Math.max(0, Number(limits[c.key]) || 0)])
      );
      await axios.put(`${API_ROOT}/shizai/kanban-settings`, { wip_limits: cleaned });
      setLimits(cleaned);
      toast.success(t('shizai.saved'));
    } catch (e) { toast.error(e?.response?.data?.detail || '保存に失敗しました'); }
    finally { setWipBusy(false); }
  };

  return (
    <div className="space-y-6 page-enter" data-testid="shizai-settings-page">
      <PageHeader
        testid="shizai-settings-header"
        title="資材 設定"
        description="発注承認・かんばんの上限・関連マスターをまとめて管理します"
      />

      <Tabs defaultValue="orders">
        <TabsList data-testid="shizai-settings-tabs">
          <TabsTrigger value="orders" data-testid="shizai-settings-tab-orders">発注・承認</TabsTrigger>
          <TabsTrigger value="kanban" data-testid="shizai-settings-tab-kanban">かんばん</TabsTrigger>
          <TabsTrigger value="related" data-testid="shizai-settings-tab-related">関連設定</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="pt-5">
          {!cfg ? (
            <p className="text-sm text-zinc-400">読み込み中…</p>
          ) : (
            <div className="max-w-2xl space-y-8">
              <FormSection title={t('shizai.approval.settings')} description="金額しきい値を超えた発注に承認を必須にします" testid="shizai-settings-approval">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium text-zinc-800">{t('shizai.approval.enable')}</Label>
                    <p className="text-xs text-zinc-500 mt-0.5">{t('shizai.approval.enableDesc')}</p>
                  </div>
                  <Switch data-testid="config-approval-enabled" disabled={!canWrite}
                    checked={!!cfg.po_approval_enabled}
                    onCheckedChange={(v) => setCfg((p) => ({ ...p, po_approval_enabled: v }))} />
                </div>
                <div>
                  <Label className="text-xs font-medium text-zinc-600">{t('shizai.approval.threshold')}</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-zinc-400">¥</span>
                    <Input type="number" min="0" data-testid="config-approval-threshold" value={cfg.po_approval_threshold}
                      disabled={!cfg.po_approval_enabled || !canWrite}
                      onChange={(e) => setCfg((p) => ({ ...p, po_approval_threshold: e.target.value }))}
                      className="h-10 text-right max-w-[220px]" />
                  </div>
                  <p className="text-[11px] text-zinc-400 mt-1">{t('shizai.approval.thresholdHint')}</p>
                </div>
              </FormSection>

              <FormSection title="発注の入力ルール" testid="shizai-settings-rules">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Label className="text-sm font-medium text-zinc-800">分類を必須にする</Label>
                    <p className="text-xs text-zinc-500 mt-0.5">オンの間、分類が未指定の発注は保存できません。</p>
                  </div>
                  <Switch data-testid="config-require-category" disabled={!canWrite}
                    checked={!!cfg.require_order_category_on_po}
                    onCheckedChange={(v) => setCfg((p) => ({ ...p, require_order_category_on_po: v }))} />
                </div>
              </FormSection>

              <FormSection title="検収写真の保存期間 (TTL)" description="期間を超えた検収写真は自動削除されます（既定 90 日 / 0=無期限）" testid="shizai-settings-ttl">
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" max="3650" data-testid="config-receipt-photo-ttl"
                    disabled={!canWrite}
                    value={cfg.receipt_photo_retention_days}
                    onChange={(e) => setCfg((p) => ({ ...p, receipt_photo_retention_days: e.target.value }))}
                    className="h-10 text-right w-28" />
                  <span className="text-sm text-zinc-500">日</span>
                </div>
              </FormSection>

              {canWrite && (
                <div className="flex justify-end">
                  <Button onClick={saveConfig} disabled={cfgBusy} data-testid="config-save-btn" className="h-10">
                    {cfgBusy ? t('shizai.saving') : t('shizai.save')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kanban" className="pt-5">
          <div className="max-w-2xl space-y-6">
            <FormSection title="WIP上限（列ごとの最大件数）" description="0 の場合は無制限です。上限を超えた列にはかんばん上で警告が表示されます。" testid="shizai-settings-wip">
              {KANBAN_COLUMNS.map((col) => (
                <div key={col.key} className="flex items-center gap-3">
                  <Label className="text-sm text-zinc-800 flex-1">{col.label}</Label>
                  <Input type="number" min={0} step={1} disabled={!canWrite}
                    value={limits[col.key] ?? 0}
                    onChange={(e) => setLimits((prev) => ({ ...prev, [col.key]: e.target.value }))}
                    data-testid={`kanban-wip-input-${col.key}`}
                    className="h-9 w-24 text-right tabular-nums" />
                  <span className="text-xs text-zinc-400 w-8">件</span>
                </div>
              ))}
            </FormSection>
            {canWrite && (
              <div className="flex justify-end">
                <Button onClick={saveWip} disabled={wipBusy} data-testid="kanban-settings-save" className="h-10">
                  {wipBusy ? t('shizai.saving') : t('shizai.save')}
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="related" className="pt-5">
          <div className="max-w-2xl grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="shizai-settings-related">
            {RELATED.map((r) => (
              <Link key={r.to} to={r.to} data-testid={`shizai-settings-link-${r.to.split('/').pop()}`}
                className="group rounded-lg border border-zinc-200 bg-white p-4 transition-[border-color,box-shadow] hover:border-teal-300 hover:shadow-sm">
                <div className="flex items-center gap-2">
                  <Gear size={15} weight="duotone" className="text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-900">{r.label}</span>
                  <ArrowRight size={13} weight="bold" className="ml-auto text-zinc-300 group-hover:text-teal-700 transition-colors" />
                </div>
                <p className="text-xs text-zinc-500 mt-1">{r.desc}</p>
              </Link>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
